import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Toolbar,
  Typography
} from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { validateBr } from 'js-brasil';
import AppNav from '../../components/appNav';
import { useEvent } from '../../context/EventContext';
import { api } from '../../services/api';
import { maskCEP, maskCNPJ, maskCPF2, maskTEL } from '../../utils/mask';
import isValidEmail from '../../utils/isValidEmail';
import { isPlanAvailable } from '../../utils/plan';

const INITIAL_COMPANY_DATA = {
  document: '',
  social_reason: '',
  name: '',
  email: '',
  address: '',
  phone: '',
  cep: '',
  city: '',
  state: '',
  district: '',
  number: '',
  plan: '',
  quantity: 1,
  payment_form: ''
};

const COMPANY_STEPS = ['Dados da Instituição', 'Tipo de Compra', 'Pagamento', 'Revisão'];
const DEFAULT_PAYMENT_OPTIONS = ['Boleto', 'Pix', 'Transação ou Depósito Bancário'];

function formatCurrency(value) {
  return Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0));
}

function Copyright() {
  return (
    <Typography variant="body2" color="text.secondary" align="center">
      {'Todos os direitos reservados © '}
      <Link color="inherit" href="https://softtechcloud.com.br/">
        SoftTech
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

export default function CompanySubscribe() {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const { events, setEvents } = useEvent();

  const [eventLoading, setEventLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [stepError, setStepError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [companyData, setCompanyData] = useState(INITIAL_COMPANY_DATA);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [personType, setPersonType] = useState('juridica');

  useEffect(() => {
    async function getEventById() {
      if (!eventId) {
        setStepError('Evento não informado');
        setEventLoading(false);
        return;
      }

      try {
        const { data } = await api.get(`/events/${eventId}?populate=plans`);
        if (!data?.isActive) {
          setStepError('Evento desativado');
          setTimeout(() => navigate(`/event/${eventId}`), 1200);
          return;
        }
        setEvents(data);
      } catch (error) {
        setStepError('Erro ao carregar dados do evento');
      } finally {
        setEventLoading(false);
      }
    }

    getEventById();

    return () => {
      setEvents(prev => (Array.isArray(prev) ? prev : []));
    };
  }, [eventId, navigate, setEvents]);

  const plans = useMemo(() => (events?.plans || []).filter(p => isPlanAvailable(p)), [events]);
  const selectedPlan = useMemo(
    () => plans.find(plan => String(plan.id) === String(companyData.plan)),
    [plans, companyData.plan]
  );

  const total = useMemo(
    () => Number(selectedPlan?.price || 0) * Number(companyData.quantity || 1),
    [selectedPlan, companyData.quantity]
  );

  const isFree = total === 0 && !!companyData.plan;

  const paymentOptions = useMemo(() => {
    if (events?.payment_options) {
      return Object.keys(events.payment_options).filter(key => events.payment_options[key]);
    }
    return DEFAULT_PAYMENT_OPTIONS;
  }, [events]);

  const currentPaymentOptions = isFree ? ['Gratuito'] : paymentOptions;

  const basePath = useMemo(() => {
    const first = window.location.pathname.split('/').filter(Boolean)[0];
    return ['apeprev', 'semper'].includes(first) ? `/${first}` : '';
  }, []);

  function handleExit() {
    navigate(`/event-custom/${eventId}`);
  }

  async function handleCPFBlur() {
    const cpf = companyData.document;
    if (!validateBr.cpf(cpf)) return;

    setCompanyLoading(true);
    try {
      const { data } = await api.post('/subscriber/form', { data: { cpf } });
      if (data) {
        setCompanyData(prev => ({
          ...prev,
          name: data.name || prev.name,
          email: data.email || prev.email,
          phone: data.whatsapp ? maskTEL(data.whatsapp) : prev.phone,
          cep: data.cep || prev.cep,
          address: data.street || prev.address,
          number: data.number || prev.number,
          district: data.neighborhood || prev.district,
          city: data.city || prev.city,
          state: data.state || prev.state,
        }));
      }
    } catch (error) {
      return;
    } finally {
      setCompanyLoading(false);
    }
  }

  async function handleDocumentBlur() {
    if (personType === 'fisica') return;
    const cnpj = companyData.document;
    if (!validateBr.cnpj(cnpj)) return;

    setCompanyLoading(true);
    try {
      const encodedCnpj = cnpj.replace('/', '%2F');
      const { data } = await api.get(`/companies/${encodedCnpj}?event=${events?.id || eventId}`);
      if (data) {
        setCompanyData(prev => ({
          ...prev,
          ...data,
          document: cnpj,
          cep: data.cep || '',
          address: data.address || '',
          number: data.number || '',
          district: data.district || '',
          city: data.city || '',
          state: data.state || '',
          plan: prev.plan,
          quantity: prev.quantity,
          payment_form: prev.payment_form
        }));
      }
    } catch (error) {
      setCompanyData(prev => ({ ...prev, document: cnpj }));
    } finally {
      setCompanyLoading(false);
    }
  }

  async function handleCepBlurInstituicao() {
    const cep = companyData.cep.replace(/\D/g, '');
    if (cep.length !== 8) return;

    setCompanyLoading(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      if (!data.erro) {
        setCompanyData(prev => ({
          ...prev,
          address: data.logradouro,
          district: data.bairro,
          city: data.localidade,
          state: data.uf
        }));
      }
    } catch (error) {
      return;
    } finally {
      setCompanyLoading(false);
    }
  }

  function validateCurrentStep() {
    setStepError('');

    if (activeStep === 0) {
      if (personType === 'juridica') {
        if (!companyData.document || !validateBr.cnpj(companyData.document)) return 'CNPJ obrigatório e válido';
        if (!companyData.social_reason) return 'Razão social obrigatória';
      } else {
        if (!companyData.document || !validateBr.cpf(companyData.document)) return 'CPF obrigatório e válido';
        if (!companyData.name) return 'Nome obrigatório';
      }
      if (!companyData.email || !isValidEmail(companyData.email)) return 'E-mail obrigatório e válido';
      if (!companyData.phone || companyData.phone.replace(/\D/g, '').length < 10) return 'Telefone obrigatório e válido';
      if (!companyData.cep || companyData.cep.replace(/\D/g, '').length !== 8) return 'CEP obrigatório e válido';
      if (!companyData.address) return 'Endereço obrigatório';
      if (!companyData.number) return 'Número obrigatório';
      if (!companyData.district) return 'Bairro obrigatório';
      if (!companyData.city) return 'Cidade obrigatória';
      if (!companyData.state) return 'Estado obrigatório';
      return '';
    }

    if (activeStep === 1) {
      if (!companyData.plan) return 'Selecione um tipo de compra';
      if (!companyData.quantity || Number(companyData.quantity) < 1) return 'Quantidade obrigatória';
      return '';
    }

    if (activeStep === 2) {
      if (!companyData.payment_form) return 'Forma de pagamento obrigatória';
      return '';
    }

    return '';
  }

  function buildAutoSubscribers(quantity) {
    const totalSubscribers = Math.max(1, Number(quantity || 1));
    const displayName = personType === 'fisica' ? companyData.name : companyData.social_reason;

    return Array.from({ length: totalSubscribers }).map((_, index) => ({
      isPassport: personType !== 'fisica' || totalSubscribers > 1,
      document: personType === 'fisica' && totalSubscribers === 1
        ? companyData.document
        : `AUTO-${companyData.document.replace(/\D/g, '').slice(0, 4)}-${index + 1}`,
      name: totalSubscribers === 1 ? displayName : `${displayName} - LIVRO ${index + 1}`,
      badge: `${displayName}`.slice(0, 30),
      whatsapp: companyData.phone,
      email: companyData.email,
      office: 'Participante',
      birth: '2000-01-01',
      gender: 'Outro',
    }));
  }

  function buildPayload() {
    return {
      event: eventId,
      company: {
        document: companyData.document,
        social_reason: personType === 'fisica' ? companyData.name : companyData.social_reason,
        email: companyData.email,
        address: companyData.address,
        phone: companyData.phone,
        cep: companyData.cep,
        city: companyData.city,
        state: companyData.state,
        district: companyData.district,
        number: companyData.number
      },
      plan: Number(companyData.plan),
      plan_price: Number(selectedPlan?.price || 0),
      quantity: Number(companyData.quantity || 1),
      payment_form: isFree ? 'Gratuito' : companyData.payment_form,
      installments: 1,
      hosting: 'NoHosting',
      daily: 0,
      subscribers: buildAutoSubscribers(companyData.quantity)
    };
  }

  async function handleSubmit() {
    setStepError('');
    setLoading(true);
    setSuccess(false);

    try {
      const { data } = await api.post('/order/installment', { data: buildPayload() });

      if (Array.isArray(data) && data.length > 0) {
        const firstPaymentUrl = data.find(item => item?.payment_url)?.payment_url;
        if (firstPaymentUrl) {
          window.open(firstPaymentUrl, '_blank');
        }
      } else if (data?.payment_url) {
        window.open(data.payment_url, '_blank');
      }

      setSuccess(true);
      setActiveStep(0);
      setCompanyData(INITIAL_COMPANY_DATA);
      setPersonType('juridica');
    } catch (error) {
      setStepError(error.response?.data?.error?.message || error.message || 'Erro ao finalizar compra');
    } finally {
      setLoading(false);
    }
  }

  function handleNext() {
    const err = validateCurrentStep();
    if (err) {
      setStepError(err);
      return;
    }
    setStepError('');
    setActiveStep(prev => Math.min(prev + 1, COMPANY_STEPS.length - 1));
  }

  function handleBack() {
    setStepError('');
    setActiveStep(prev => Math.max(prev - 1, 0));
  }

  function renderStepContent() {
    if (activeStep === 0) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <RadioGroup
              row
              value={personType}
              onChange={e => {
                setPersonType(e.target.value);
                setCompanyData(INITIAL_COMPANY_DATA);
              }}
            >
              <FormControlLabel value="juridica" control={<Radio />} label="Pessoa Jurídica" />
              <FormControlLabel value="fisica" control={<Radio />} label="Pessoa Física" />
            </RadioGroup>
          </Grid>
          {personType === 'juridica' ? (
            <>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="CNPJ"
                  fullWidth
                  value={companyData.document}
                  onChange={e => setCompanyData({ ...companyData, document: maskCNPJ(e.target.value) })}
                  onBlur={handleDocumentBlur}
                  error={!!companyData.document && !validateBr.cnpj(companyData.document)}
                  helperText={companyData.document && !validateBr.cnpj(companyData.document) ? 'CNPJ inválido' : 'Digite o CNPJ e saia do campo para buscar'}
                  InputProps={{ endAdornment: companyLoading && <CircularProgress size={20} /> }}
                  inputProps={{ maxLength: 18 }}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField label="Razão Social" fullWidth value={companyData.social_reason} onChange={e => setCompanyData({ ...companyData, social_reason: e.target.value })} />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="CPF"
                  fullWidth
                  value={companyData.document}
                  onChange={e => setCompanyData({ ...companyData, document: maskCPF2(e.target.value) })}
                  onBlur={handleCPFBlur}
                  error={!!companyData.document && !validateBr.cpf(companyData.document)}
                  helperText={companyData.document && !validateBr.cpf(companyData.document) ? 'CPF inválido' : 'Digite o CPF e saia do campo para buscar'}
                  InputProps={{ endAdornment: companyLoading && <CircularProgress size={20} /> }}
                  inputProps={{ maxLength: 14 }}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField label="Nome Completo" fullWidth value={companyData.name} onChange={e => setCompanyData({ ...companyData, name: e.target.value })} />
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={6}>
            <TextField label="E-mail" fullWidth value={companyData.email} onChange={e => setCompanyData({ ...companyData, email: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Telefone" fullWidth value={companyData.phone} onChange={e => setCompanyData({ ...companyData, phone: maskTEL(e.target.value) })} inputProps={{ maxLength: 15 }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="CEP" fullWidth value={companyData.cep} onChange={e => setCompanyData({ ...companyData, cep: maskCEP(e.target.value) })} onBlur={handleCepBlurInstituicao} inputProps={{ maxLength: 9 }} />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField label="Endereço" fullWidth value={companyData.address} onChange={e => setCompanyData({ ...companyData, address: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField label="Número" fullWidth value={companyData.number} onChange={e => setCompanyData({ ...companyData, number: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField label="Bairro" fullWidth value={companyData.district} onChange={e => setCompanyData({ ...companyData, district: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Cidade" fullWidth value={companyData.city} onChange={e => setCompanyData({ ...companyData, city: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField label="Estado" fullWidth value={companyData.state} onChange={e => setCompanyData({ ...companyData, state: e.target.value })} />
          </Grid>
        </Grid>
      );
    }

    if (activeStep === 1) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <FormControl fullWidth>
              <InputLabel id="company-plan-label">Tipo de Compra</InputLabel>
              <Select
                labelId="company-plan-label"
                value={companyData.plan}
                label="Tipo de Compra"
                onChange={e => {
                  const selected = plans.find(p => String(p.id) === String(e.target.value));
                  const selectedIsFree = Number(selected?.price || 0) === 0;
                  setCompanyData(prev => ({
                    ...prev,
                    plan: e.target.value,
                    payment_form: selectedIsFree ? 'Gratuito' : (prev.payment_form === 'Gratuito' ? '' : prev.payment_form)
                  }));
                }}
              >
                <MenuItem value="">Selecione</MenuItem>
                {plans.filter(plan => isPlanAvailable(plan)).map(plan => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.type} - {formatCurrency(plan.price)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Quantidade"
              type="number"
              fullWidth
              value={companyData.quantity}
              onChange={e => setCompanyData(prev => ({ ...prev, quantity: Math.max(1, Number(e.target.value || 1)) }))}
              inputProps={{ min: 1 }}
            />
          </Grid>
        </Grid>
      );
    }

    if (activeStep === 2) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="company-payment-label">Forma de Pagamento</InputLabel>
              <Select
                labelId="company-payment-label"
                value={companyData.payment_form}
                label="Forma de Pagamento"
                onChange={e => setCompanyData(prev => ({ ...prev, payment_form: e.target.value }))}
              >
                <MenuItem value="">Selecione</MenuItem>
                {currentPaymentOptions.filter(option => option.status !== false).map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      );
    }

    return (
      <Box>
        <Typography variant="h6" gutterBottom>Resumo da Compra</Typography>
        <Typography><b>{personType === 'fisica' ? 'Nome' : 'Instituição'}:</b> {personType === 'fisica' ? companyData.name : companyData.social_reason}</Typography>
        <Typography><b>{personType === 'fisica' ? 'CPF' : 'CNPJ'}:</b> {companyData.document}</Typography>
        <Typography><b>Plano:</b> {selectedPlan?.type || '-'}</Typography>
        <Typography><b>Quantidade:</b> {companyData.quantity}</Typography>
        <Typography><b>Total:</b> {formatCurrency(total)}</Typography>
        <Typography><b>Pagamento:</b> {companyData.payment_form || '-'}</Typography>
      </Box>
    );
  }

  if (eventLoading) {
    return (
      <Box sx={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <AppNav />
      <AppBar
        position="relative"
        color="default"
        elevation={0}
        sx={{
          position: 'relative',
          borderBottom: theme => `1px solid ${theme.palette.divider}`
        }}
      >
        <Toolbar>
          <Typography variant="h6" color="inherit" noWrap>
            {events?.name || 'Evento'}
          </Typography>
        </Toolbar>
      </AppBar>

      <Stack direction="row" py={3} sx={{ alignItems: 'center' }}>
        <IconButton onClick={handleExit}>
          <ArrowBack color="error" />
        </IconButton>
        <Typography color="error">Sair</Typography>
      </Stack>

      <Container component="main" maxWidth="xl" sx={{ mb: 4, width: '100%' }}>
        <Paper variant="outlined" sx={{ my: { xs: 2, md: 2 }, p: { xs: 2, md: 3 } }}>
          <Typography component="h1" variant="h4" align="center" sx={{ mb: 2 }}>
            Comprar
          </Typography>

          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {COMPANY_STEPS.map(step => (
              <Step key={step}>
                <StepLabel>{step}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Compra registrada com sucesso. Se houver boleto, ele foi aberto em uma nova aba.
            </Alert>
          )}

          {stepError && <Typography color="error" sx={{ mt: 1, mb: 2 }}>{stepError}</Typography>}

          <Box sx={{ mt: 2 }}>{renderStepContent()}</Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button variant="outlined" disabled={activeStep === 0 || loading} onClick={handleBack}>
              Voltar
            </Button>

            {activeStep < COMPANY_STEPS.length - 1 ? (
              <Button variant="contained" onClick={handleNext} disabled={loading}>
                Próximo
              </Button>
            ) : (
              <Button
                variant="contained"
                color="success"
                disabled={loading}
                onClick={async () => {
                  const err = validateCurrentStep();
                  if (err) {
                    setStepError(err);
                    return;
                  }
                  await handleSubmit();
                }}
              >
                {loading ? 'Finalizando...' : 'Finalizar compra'}
              </Button>
            )}
          </Box>
        </Paper>

        <Copyright />
      </Container>
    </>
  );
}
